import type { ClientBuilder } from "api-types/builders/clientBuilderTypes";
import type { ClientLocationsPortalData, ClientLocationWithLevel } from "api-types/locations/locationsResponses";
import {
  buildLocationSelectTree,
  treeToSelectOptions,
} from "lib/components/builders/communities/utils/locationSelectTree";
import { getBuilderLocationsURL } from "lib/endpointHelpers";
import { useFetch } from "lib/fetcher";
import { useMemo } from "react";
import Select from "ui/forms/Select";

interface ChooseLocationProps {
  builder: ClientBuilder;
  selectedLocation: ClientLocationWithLevel | null;
  onSelect: (location: ClientLocationWithLevel | null) => void;
}

export function ChooseLocation({ builder, selectedLocation, onSelect }: ChooseLocationProps) {
  // Always allow moving between any location within the builder
  const { data: allLocations } = useFetch<ClientLocationsPortalData>(
    getBuilderLocationsURL({ builderId: builder.id }),
    [builder.id],
  );

  const locations: ClientLocationWithLevel[] = useMemo(() => allLocations?.locations ?? [], [allLocations]);

  const locationsById = useMemo(() => {
    return new Map(locations.map((location) => [location.id, location]));
  }, [locations]);

  const options = useMemo(() => {
    const tree = buildLocationSelectTree(locations, {
      disableMultiUnit: true,
      disableWhenHomePlansNotAllowed: true,
    });
    return treeToSelectOptions(tree);
  }, [locations]);

  return (
    <Select
      clearable
      placeholder="Search or select a location..."
      value={selectedLocation?.id ?? null}
      options={options}
      optionsMaxHeight={320}
      onChange={(value) => {
        onSelect(value === null ? null : locationsById.get(String(value)) ?? null);
      }}
    />
  );
}
